/**
 * Organization service for frontend.
 *
 * Handles organization management via auth backend endpoints.
 * Implements Task 012: Onboarding & Organization Flow.
 */

import type { CompanyData } from '@/lib/company-lookup.service'

export interface OnboardingProgress {
  status: 'not_started' | 'in_progress' | 'completed' | 'postponed'
  current_step: string | null
  completed_steps: string[]
  completed_at: string | null
  postponed_at: string | null
}

export interface Organization {
  id: string
  name: string
  slug: string | null
  logo_url: string | null
  status: string
  billing_tier: string
  is_demo: boolean
  company_data: CompanyData | null
  created_at: string
  role: string | null
  is_owner: boolean
}

export interface MyOrganizationsResponse {
  organizations: Organization[]
  needs_onboarding: boolean
  /** True when the response came from the API; false on network/server error. */
  is_authoritative: boolean
}

export interface CreateOrganizationRequest {
  name: string
  slug?: string
  logo_url?: string
  is_demo?: boolean
  user_name?: string
  company_data?: CompanyData
}

export interface UpdateOrganizationRequest {
  name?: string
  logo_url?: string
  company_data?: CompanyData
}

export interface JoinOrganizationRequest {
  invite_code: string
  user_name?: string
}

export interface Invitation {
  id: string
  code: string
  email: string | null
  role: string
  max_uses: number | null
  uses: number
  expires_at: string | null
  is_active: boolean
  created_at: string
}

export interface CreateInvitationRequest {
  email?: string
  role?: 'admin' | 'member'
  max_uses?: number
  expires_in_days?: number
}

export interface Member {
  id: string
  user_email: string | null
  user_name: string | null
  role: string
  is_owner: boolean
  status: string
  joined_at: string
}

class OrganizationService {
  private readonly apiBaseUrl: string

  constructor() {
    // Use empty string to leverage Vite proxy for /organizations/* routes
    this.apiBaseUrl = ''
  }

  /**
   * Get all organizations the current user belongs to.
   * Also returns whether user needs onboarding (has no orgs).
   */
  async getMyOrganizations(): Promise<MyOrganizationsResponse> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/organizations/my`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 401) {
        throw new Error('Not authenticated')
      }

      // Handle 404 gracefully - endpoint may not exist yet
      if (response.status === 404) {
        console.warn('Organization endpoint not available')
        return { organizations: [], needs_onboarding: true, is_authoritative: true }
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.detail || 'Failed to fetch organizations')
      }

      const data = await response.json()
      return { ...data, is_authoritative: true }
    } catch (err) {
      // Network/server errors — caller must decide what to do.
      // Do NOT fake a "needs onboarding" response on transient failures.
      console.warn('Failed to fetch organizations:', err)
      return { organizations: [], needs_onboarding: false, is_authoritative: false }
    }
  }

  /**
   * Create a new organization.
   * The creating user becomes the owner.
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await fetch(`${this.apiBaseUrl}/organizations`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to create organization')
    }

    return response.json()
  }

  /**
   * Join an organization using an invite code.
   */
  async joinOrganization(data: JoinOrganizationRequest): Promise<Organization> {
    const response = await fetch(`${this.apiBaseUrl}/organizations/join`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 404) {
      throw new Error('Invalid or expired invitation code')
    }

    if (response.status === 403) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'You cannot use this invitation')
    }

    if (response.status === 409) {
      throw new Error('You are already a member of this organization')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to join organization')
    }

    return response.json()
  }

  /**
   * Get organization details.
   * User must be a member of the organization.
   */
  async getOrganization(orgId: string): Promise<Organization> {
    const response = await fetch(`${this.apiBaseUrl}/organizations/${orgId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      throw new Error('You are not a member of this organization')
    }

    if (response.status === 404) {
      throw new Error('Organization not found')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to fetch organization')
    }

    return response.json()
  }

  /**
   * Update organization settings (name, logo, company data).
   * Only admins and owners can update.
   */
  async updateOrganization(orgId: string, data: UpdateOrganizationRequest): Promise<Organization> {
    const response = await fetch(`${this.apiBaseUrl}/organizations/${orgId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      throw new Error('Only owners and admins can update organization settings')
    }

    if (response.status === 404) {
      throw new Error('Organization not found')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to update organization')
    }

    return response.json()
  }

  /**
   * Create an invitation to join the organization.
   * Only admins and owners can create invitations.
   */
  async createInvitation(orgId: string, data: CreateInvitationRequest): Promise<Invitation> {
    const response = await fetch(`${this.apiBaseUrl}/organizations/${orgId}/invitations`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      throw new Error('Only owners and admins can create invitations')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to create invitation')
    }

    return response.json()
  }

  /**
   * List invitations for an organization.
   * Only admins and owners can view invitations.
   */
  async listInvitations(orgId: string, activeOnly: boolean = true): Promise<Invitation[]> {
    const params = new URLSearchParams()
    params.set('active_only', String(activeOnly))

    const response = await fetch(
      `${this.apiBaseUrl}/organizations/${orgId}/invitations?${params}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      throw new Error('Only owners and admins can view invitations')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to fetch invitations')
    }

    return response.json()
  }

  /**
   * Deactivate an invitation.
   * Only admins and owners can deactivate invitations.
   */
  async deactivateInvitation(orgId: string, invitationId: string): Promise<void> {
    const response = await fetch(
      `${this.apiBaseUrl}/organizations/${orgId}/invitations/${invitationId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      throw new Error('Only owners and admins can deactivate invitations')
    }

    if (response.status === 404) {
      throw new Error('Invitation not found')
    }

    if (!response.ok && response.status !== 204) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to deactivate invitation')
    }
  }

  /**
   * List organization members.
   */
  async listMembers(orgId: string, limit: number = 50, offset: number = 0): Promise<Member[]> {
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    params.set('offset', String(offset))

    const response = await fetch(
      `${this.apiBaseUrl}/organizations/${orgId}/members?${params}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      throw new Error('You are not a member of this organization')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to fetch members')
    }

    return response.json()
  }

  /**
   * Get onboarding progress for an organization.
   * Returns which steps are complete so we can resume after refresh.
   */
  async getOnboardingProgress(orgId: string): Promise<OnboardingProgress> {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/organizations/${orgId}/onboarding`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      if (!response.ok) {
        return { status: 'not_started', current_step: null, completed_steps: [], completed_at: null, postponed_at: null }
      }

      return response.json()
    } catch {
      return { status: 'not_started', current_step: null, completed_steps: [], completed_at: null, postponed_at: null }
    }
  }

  /**
   * Remove a member from the organization.
   */
  async removeMember(orgId: string, membershipId: string): Promise<void> {
    const response = await fetch(
      `${this.apiBaseUrl}/organizations/${orgId}/members/${membershipId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (response.status === 401) {
      throw new Error('Not authenticated')
    }

    if (response.status === 403) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'You cannot remove this member')
    }

    if (response.status === 404) {
      throw new Error('Member not found')
    }

    if (!response.ok && response.status !== 204) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to remove member')
    }
  }
}

export const organizationService = new OrganizationService()
export default organizationService
